import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  Audience,
  BatchMode,
  ChannelType,
  EnrollSourceType,
  ExperienceAnchorType,
  ExperienceStatus,
  ExperienceTemplateMessage,
  ExperienceTemplateStatus,
  IExperience,
  IExperienceTemplate,
  ISupportingActor,
  ITaskTemplate,
  PARTICIPANT_ACTOR_KEY,
  SupportingActorType,
  TaskBlockTemplate,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { Integration } from '@dadohr/shared/domainTypes/integrations';
import {
  IExperienceGenericMessageMail,
  IExperienceMessagePerCohortAggregated,
  IExperienceMessagePerCohortUnnested,
  ITaskStartMail,
  ITaskStartPerCohort,
  PerCohortTaskItem
} from '@dadohr/shared/domainTypes/mail';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import { Button, Popover, TextField } from '@material-ui/core';
import { validate as isValidEmail } from 'email-validator';
import { fromPairs, keyBy, omit, sortBy } from 'lodash';
import { ChevronDown, Mail, MessageSquare, Send, Slack } from 'lucide-react';
import moment from 'moment-timezone';
import { useMemo, useRef, useState } from 'react';
import { AutocompleteUser } from '../../../../components/AutocompleteUser';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { NewUxButton } from '../../components/Builders/newux/kit/Button';
import { CustomInputLabel } from '../../../../components/CustomInputLabel';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../i18next';
import { ReactComponent as TeamsIcon } from '../../../../icons/microsoft_teams_icon.svg';
import { FlexContainer, FlexContainerVertical } from '../../../../layout/Flex';
import { useCurrentUser } from '../../../../services/currentUser';
import { getAudienceForUser } from '../../../../services/experiences/audience';
import { toDenormalizedExperience } from '../../../../services/experiences/instance';
import {
  approximateIfAggregationForMessageIsPossible,
  collectSupportingActorsForParticipant,
  createExperienceInstance,
  getAnchorInterpolationVariable
} from '../../../../services/experiences/template';
import {
  getFixedOrConvertToMessageFixedTimingConsideringTriggerChain,
  getFixedOrConvertToTaskFixedTimingConsideringTriggerChain,
  getFreshNonTaskStartDate,
  getFreshTaskDates
} from '../../../../services/experiences/timing';
import { useFeatureEnabled } from '../../../../services/features';
import { useIntegrationsContext } from '../../../../services/integrations/context';
import { sendPreviewMail } from '../../../../services/mail';
import { sendPreviewTeamsMessage } from '../../../../services/microsoft';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { analyzeExperienceInterpolationsInBlocks } from '../../../../services/set/interpolation';
import { sendPreviewSlackMessage } from '../../../../services/slack';
import { sendSmsPreview } from '../../../../services/sms';
import { addToTs, fromMoment, now } from '../../../../services/time';
import { getUsersByOrgId, toFullName } from '../../../../services/user';
import { useAnchorFields } from '../../components/Anchors';

const hasAudienceWithActiveBlocksForUser = (
  audiences: Audience[],
  user: Doc<IUser>,
  referenceDate: Timestamp
) => {
  return getAudienceForUser(audiences, user, referenceDate).blocks.length > 0;
};

const PopoverContainer = styled('div')((p) => ({
  padding: p.theme.spacing(3),
  minWidth: 600,
  maxWidth: 700
}));

export const SendTestButton = ({
  orgId,
  recipientActors,
  validateParticipant,
  sendMail,
  sendSlack,
  sendTeams,
  sendSms,
  additionalInfo,
  newUx = false
}: {
  orgId: string;
  recipientActors: { [key: string]: ISupportingActor };
  validateParticipant: (user: Doc<IUser>) => string | null | undefined; // returns an error message if necessary
  sendMail?: (
    sampleUser: Doc<IUser>,
    recipientEmail: string,
    otherActors: { [actorKey: string]: Doc<IUser> } | null
  ) => Promise<any>;
  sendSlack?: (
    sampleUser: Doc<IUser>,
    recipientEmail: string,
    otherActors: { [actorKey: string]: Doc<IUser> } | null
  ) => Promise<any>;
  sendTeams?: (
    sampleUser: Doc<IUser>,
    recipientEmail: string,
    otherActors: { [actorKey: string]: Doc<IUser> } | null
  ) => Promise<any>;
  sendSms?: (
    sampleUser: Doc<IUser>,
    phoneNumber: string,
    otherActors: { [actorKey: string]: Doc<IUser> } | null
  ) => Promise<any>;
  additionalInfo?: React.ReactNode;
  newUx?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const currUser = useCurrentUser();
  const [sampleUser, setSampleUser] = useState<Doc<IUser> | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  const [selectedActors, setSelectedActors] = useState<{
    [actorKey: string]: Doc<IUser>;
  }>({});

  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();

  const [targetEmail, setTargetEmail] = useState(currUser.email);

  const autoCompleteUserTestData = async (participant: Doc<IUser> | null) => {
    // Participant is deselected clear fields and ensure they're disabled.
    if (participant == null) {
      setSampleUser(null);
      setSelectedActors({});
      return;
    }

    //Update otherActor data and manager dropdown
    setSampleUser(participant);
    const allOtherActors = await getAllActorsForParticipant(
      orgId,
      recipientActors,
      participant
    );
    const nextOtherActors = fromPairs(
      allOtherActors.supportingActors.map((ac) => [ac.key, ac.user])
    );
    //Update Selected Actors With Supporting Actor from Participant
    setSelectedActors(nextOtherActors);
  };

  const areAllRequiredSupportingActorsAssigned =
    Object.values(recipientActors).filter(
      (actor) => actor.type !== SupportingActorType.CHANNEL
    ).length === Object.keys(selectedActors).length;

  const invalidParticipantText = sampleUser && validateParticipant(sampleUser);

  return (
    <>
      {newUx ? (
        // Match the participant-dialog header buttons (kit NewUxButton).
        <NewUxButton
          variant="outlined"
          color="neutral"
          size="medium"
          onClick={() => setOpen((x) => !x)}
          startIcon={<Send size={15} />}
          ref={ref}
        >
          {t('Send test')}
        </NewUxButton>
      ) : (
        <Button
          variant="text"
          size="small"
          onClick={() => setOpen((x) => !x)}
          endIcon={<ChevronDown size={16} />}
          ref={ref}
        >
          {t('Send Test')}
        </Button>
      )}

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={ref.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <PopoverContainer>
          <FlexContainerVertical spacing={2}>
            <CustomInputLabel
              fullWidth
              label={t('Send test to')}
              subLabel={
                sendSms
                  ? t('Enter an email address or US phone number')
                  : t('Enter an email address')
              }
            >
              <TextField
                fullWidth
                value={targetEmail}
                onChange={(ev) => setTargetEmail(ev.target.value)}
              />
            </CustomInputLabel>
            <CustomInputLabel
              fullWidth
              label={t('Sample Participant')}
              subLabel={t(
                'Data from this employee will be used for variables and target audiences'
              )}
            >
              <AutocompleteUser
                label=""
                value={sampleUser}
                onChange={autoCompleteUserTestData}
                orgId={orgId}
                error={!!invalidParticipantText}
                helperText={invalidParticipantText}
              />
            </CustomInputLabel>

            {sortBy(Object.values(recipientActors), (actor) => actor.name)
              .filter((actor) => actor.type !== SupportingActorType.CHANNEL)
              .map((actor) => {
                const selectedActor = selectedActors[actor.key];
                return (
                  <CustomInputLabel
                    key={actor.key}
                    label={actor.name}
                    subLabel={t(
                      'Data from this employee will be used for variables'
                    )}
                    fullWidth
                  >
                    <AutocompleteUser
                      label=""
                      orgId={orgId}
                      disabled={!sampleUser}
                      error={!!(sampleUser && !selectedActor)}
                      helperText={
                        sampleUser &&
                        !selectedActor &&
                        `${t(
                          `{{ name }} has no {{ actor }} assgined. Please select one to test this message. This will not change {{ preferredName }}'s actual {{ actor }}.`,
                          {
                            name: toFullName(sampleUser.data),
                            preferredName: sampleUser.data.preferredName,
                            actor: actor.name
                          }
                        )}`
                      }
                      value={selectedActor}
                      onChange={(nextActor) => {
                        if (nextActor) {
                          setSelectedActors((prevActors) => ({
                            ...prevActors,
                            [actor.key]: nextActor
                          }));
                        } else {
                          setSelectedActors((prevActors) =>
                            omit(prevActors, [actor.key])
                          );
                        }
                      }}
                    />
                  </CustomInputLabel>
                );
              })}
            {additionalInfo && <div>{additionalInfo}</div>}
            <FlexContainer justifyContent="flex-end" fullWidth>
              {sendMail && (
                <ButtonWithPromise
                  startIcon={<Mail size={14} />}
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={
                    !sampleUser ||
                    !areAllRequiredSupportingActorsAssigned ||
                    !!invalidParticipantText
                  }
                  onClick={async () => {
                    if (!sampleUser) {
                      return;
                    }
                    if (!isValidEmail(targetEmail)) {
                      enqueueSnackbarError(
                        t('Please provide a valid email address')
                      );
                      return;
                    }
                    return sendMail(
                      sampleUser,
                      targetEmail,
                      selectedActors
                    ).then(
                      () => {
                        enqueueSnackbarSuccess(
                          t(`Sent email to ${targetEmail}`)
                        );
                        setSampleUser(null);
                        setSelectedActors({});
                      },
                      (err) => {
                        console.log('Send test email error', err);
                        enqueueSnackbarError(t('Something went wrong'));
                      }
                    );
                  }}
                  pending={t('Sending email...')}
                >
                  {t('Send Email')}
                </ButtonWithPromise>
              )}
              {sendSlack && (
                <ButtonWithPromise
                  startIcon={<Slack size={14} />}
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={
                    !sampleUser ||
                    !areAllRequiredSupportingActorsAssigned ||
                    !!invalidParticipantText
                  }
                  onClick={async () => {
                    if (!sampleUser) {
                      return;
                    }
                    if (!isValidEmail(targetEmail)) {
                      enqueueSnackbarError(
                        t('Please provide a valid email address')
                      );
                      return;
                    }
                    return sendSlack(
                      sampleUser,
                      targetEmail,
                      selectedActors
                    ).then(
                      () => {
                        enqueueSnackbarSuccess(
                          t(`Sent slack message to ${targetEmail}`)
                        );
                        setSampleUser(null);
                        setSelectedActors({});
                      },
                      (err) => {
                        console.log(err, err?.message);
                        if (
                          err?.message.includes('user') &&
                          err?.message?.includes('not_found')
                        ) {
                          enqueueSnackbarError(
                            t(
                              `Could not find ${targetEmail} in your Slack workspace`
                            )
                          );
                        } else {
                          enqueueSnackbarError(t('Something went wrong'));
                        }
                      }
                    );
                  }}
                  pending={t('Sending slack...')}
                >
                  {t('Send Slack')}
                </ButtonWithPromise>
              )}

              {sendTeams && (
                <ButtonWithPromise
                  startIcon={<TeamsIcon height={14} width={14} />}
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={
                    !sampleUser ||
                    !areAllRequiredSupportingActorsAssigned ||
                    !!invalidParticipantText
                  }
                  onClick={async () => {
                    if (!sampleUser) {
                      return;
                    }
                    if (!isValidEmail(targetEmail)) {
                      enqueueSnackbarError(
                        t('Please provide a valid email address')
                      );
                      return;
                    }
                    return sendTeams(
                      sampleUser,
                      targetEmail,
                      selectedActors
                    ).then(
                      () => {
                        enqueueSnackbarSuccess(
                          t(`Sent teams message to ${targetEmail}`)
                        );
                        setSampleUser(null);
                        setSelectedActors({});
                      },
                      (err) => {
                        console.log(err, err?.message);
                        if (
                          (err?.message.includes('user') &&
                            err?.message?.includes('not_found')) ||
                          err?.message?.includes('CONVERSATION ID NOT FOUND')
                        ) {
                          enqueueSnackbarError(
                            t(
                              `Could not find ${targetEmail} in your Teams workspace`
                            )
                          );
                        } else {
                          enqueueSnackbarError(t('Something went wrong'));
                        }
                      }
                    );
                  }}
                  pending={t('Sending teams...')}
                >
                  {t('Send Teams')}
                </ButtonWithPromise>
              )}

              {sendSms && (
                <ButtonWithPromise
                  startIcon={<MessageSquare size={14} />}
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={
                    !sampleUser ||
                    !areAllRequiredSupportingActorsAssigned ||
                    !!invalidParticipantText
                  }
                  onClick={async () => {
                    if (!sampleUser) {
                      return;
                    }
                    if (
                      !targetEmail.startsWith('+1') &&
                      !targetEmail.startsWith('+43660467') &&
                      !targetEmail.startsWith('+4915155')
                    ) {
                      enqueueSnackbarError(
                        t('Please provide a valid US phone number')
                      );
                      return;
                    }
                    return sendSms(
                      sampleUser,
                      targetEmail,
                      selectedActors
                    ).then(
                      () => {
                        enqueueSnackbarSuccess(t(`Sent SMS to ${targetEmail}`));
                        setSampleUser(null);
                        setSelectedActors({});
                      },
                      (err) => {
                        console.log(err, err?.message);
                        enqueueSnackbarError(t('Something went wrong'));
                      }
                    );
                  }}
                  pending={t('Sending SMS...')}
                >
                  {t('Send SMS')}
                </ButtonWithPromise>
              )}
            </FlexContainer>
          </FlexContainerVertical>
        </PopoverContainer>
      </Popover>
    </>
  );
};

// Saves which actors have been used within message text blocks to reference later
export const getActorsFromBlocks = (
  blocks: TaskBlockTemplate[]
): {
  actors: Set<string>;
  actorsAndTheirVariables: {
    [actorKey: string]: Set<string>;
  };
} => {
  const x = analyzeExperienceInterpolationsInBlocks(blocks);
  return {
    actors: new Set(Object.keys(x.actorVariables)),
    actorsAndTheirVariables: x.actorVariables
  };
};

const getAllActorsForParticipant = async (
  orgId: string,
  supportingActorDefinitions: {
    [actorKey: string]: ISupportingActor;
  },
  participant: Doc<IUser>
) => {
  const allUsers = await getUsersByOrgId(orgId); // should be loaded by this point aready
  const usersById = keyBy(allUsers, (u) => u.id);
  return collectSupportingActorsForParticipant(
    Object.values(supportingActorDefinitions),
    usersById,
    participant
  );
};

const getAllActors = async (
  d: Doc<IExperienceTemplate>,
  participant: Doc<IUser>
) => {
  const { supportingActors, missingActors } = await getAllActorsForParticipant(
    d.data.orgId,
    d.data.supportingActors,
    participant
  );
  if (missingActors.length) {
    console.warn('ACTORS_MISSING', { d, missingActors });
  }
  const allActors = [
    {
      key: PARTICIPANT_ACTOR_KEY,
      user: participant
    },
    ...supportingActors,
    ...missingActors.map((key) => ({
      key,
      user: null
    }))
  ];
  return allActors;
};

const keyKnownActorsByKey = (
  actors: {
    key: string;
    user: Doc<IUser> | null;
  }[]
) => {
  return actors.reduce<{
    [actorKey: string]: Doc<IUser>;
  }>((m, a) => {
    if (a.user) {
      m[a.key] = a.user;
    }
    return m;
  }, {});
};

const mockRunningInstanceState = (instance: Doc<IExperience>, n: Timestamp) => {
  instance.data.status = ExperienceStatus.RUNNING;
  instance.data.tasks.forEach((task) => {
    const diff = task.timing.start.toMillis() - n.toMillis();
    task.status =
      diff < 0
        ? TaskStatus.COMPLETE
        : diff === 0
        ? TaskStatus.RUNNING
        : TaskStatus.NOT_STARTED;
  });
};

// This is needed in respect to bundling tasks with messags, when tasks
// have a specific start time associated with them.
// We need to calibrate a sensible now date for the grouping to work properly,
// otherwise we can end up in a situation where e.g. we try to send a test message
// at 6pm, but a task on the same date has a pinned start time of 9am - in this
// case the task would have already been sent from the system's perspective and is
// therefore not included in the message.
// This is a more general problem with start times not aligning with bundled items.
// There are configurations where the bundling just wouldn't work.
// We can either:
// - Rely a bit on the fact that people don't set start times to frequently anyway
//   In this case we should at least improve the preview grouping, so that it also accounts
//   for this misalignment (currently it really only checks for the same day)
// - Have the grouping mechanism ignore times altogether to simplify the system
//
// Especially when manual enrollment is done, this problem can always arise, hence further
// action will be needed.
const findIdealPreviewNowDate = (
  d: Doc<IExperienceTemplate>,
  org: IOrganization
): Timestamp => {
  const { anchor } = d.data;
  if (anchor.type === ExperienceAnchorType.FIXED) {
    return anchor.ts;
  }
  if (anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
    return fromMoment(
      moment()
        .tz(d.data.tz)
        .hour(
          anchor.offset.type === 'ABSOLUTE'
            ? anchor.offset.hour
            : org.defaultExperienceStartHour
        )
        .minute(
          anchor.offset.type === 'ABSOLUTE' ? anchor.offset.minute || 0 : 0
        )
        .second(0)
    );
  }
  return fromMoment(moment().startOf('h'));
};

export const SendTestMessageButton = ({
  d,
  msgTpl,
  disabled,
  newUx = false
}: {
  d: Doc<IExperienceTemplate>;
  msgTpl: ExperienceTemplateMessage;
  // TODO - allow other Mail types
  disabled?: boolean;
  newUx?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const currUser = useCurrentUser();
  const { active: activeIntegrations } = useIntegrationsContext();
  const slackEnabled = activeIntegrations.has(Integration.SLACK);
  const { trackEvent } = useProductAnalytics();
  const teamsEnabled = activeIntegrations.has(Integration.MICROSOFT);
  const smsEnabled = useFeatureEnabled('SMS_V1');
  const anchorFields = useAnchorFields(currUser.org.customUserFields);

  const showRepeatHint = useMemo(() => {
    return (
      msgTpl.batchMode === BatchMode.PER_COHORT &&
      !approximateIfAggregationForMessageIsPossible(
        msgTpl,
        getAnchorInterpolationVariable(d.data.anchor, anchorFields)
      )
    );
  }, [msgTpl]);

  const hasSlackChannelActor = () => {
    const actor = d.data.supportingActors[msgTpl.actor] as
      | ISupportingActor
      | undefined;
    return (
      actor?.type === SupportingActorType.CHANNEL &&
      actor.channeltype === ChannelType.SLACK
    );
  };

  const { sendTestMail, sendSlack, sendTeams, sendSms } = useMemo(() => {
    const toParams = async (
      sampleUser: Doc<IUser>,
      otherActors: { [actorKey: string]: Doc<IUser> } | null
    ) => {
      const adjustedTemplate: Doc<IExperienceTemplate> = {
        ...d,
        data: {
          ...d.data,
          messages: {
            ...d.data.messages,
            other: [...d.data.messages.other]
          }
        }
      };
      const { messages } = adjustedTemplate.data;
      if (messages.outro?.id === msgTpl.id) {
        messages.outro = msgTpl;
      } else {
        const otherMessages = messages.other || [];
        const i = otherMessages.findIndex((o) => o.id === msgTpl.id);
        if (i === -1) {
          otherMessages.push(msgTpl);
        } else {
          otherMessages[i] = msgTpl;
        }
        messages.other = otherMessages;
      }

      const n = findIdealPreviewNowDate(d, currUser.org);

      // we just want to compute where this item would sit in the timeline
      // we won't take any anchoring into account on purpose to keep this simpler

      const timing = getFixedOrConvertToMessageFixedTimingConsideringTriggerChain(
        msgTpl.timing,
        d.data
      );

      const sendAt = getFreshNonTaskStartDate(
        timing,
        n,
        {
          type: ExperienceAnchorType.IMMEDIATE,
          offset: { type: 'ABSOLUTE', duration: 0, hour: 9 },
          autoEnrollmentRejectionThreshold: null,
          v: 2
        },
        d.data.tz
      );
      const diffToNow = sendAt.toMillis() - n.toMillis();
      const adjustedStartsAt = addToTs(n, -diffToNow);

      const allActorsWithCustomChoices = await getAllActors(d, sampleUser).then(
        (acs) =>
          acs.map((actor) => {
            if (otherActors?.[actor.key]) {
              return {
                key: actor.key,
                user: otherActors[actor.key]
              };
            }
            return actor;
          })
      );

      // manager is missing in getAllActors
      console.log(allActorsWithCustomChoices, otherActors);
      const instance = createExperienceInstance(
        d,
        allActorsWithCustomChoices,
        adjustedStartsAt,
        now(),
        { type: EnrollSourceType.UNKNOWN },
        { includeTriggeredItemsAsFixedTimmingItems: true }
      );
      adjustedTemplate.data.status = ExperienceTemplateStatus.ACTIVE;
      mockRunningInstanceState(instance, n);
      const knownActorsByKey = keyKnownActorsByKey(allActorsWithCustomChoices);

      const org = {
        id: currUser.org.id,
        collection: FS.orgs,
        data: currUser.org
      };

      const denormalizedExperience = toDenormalizedExperience(
        msgTpl.actor,
        instance,
        adjustedTemplate,
        org,
        knownActorsByKey,
        {},
        { ignoreCurrentTime: true, includeSkippedItems: true }
      );

      const msg = denormalizedExperience.data.messages.find(
        (m) => m.templateId === msgTpl.id
      );
      if (!msg) {
        console.warn('Denormalized message not found', {
          denormalizedExperience,
          msgTpl
        });
        throw new Error();
      }

      if (msgTpl.batchMode === BatchMode.PER_COHORT) {
        const aggregation = approximateIfAggregationForMessageIsPossible(
          msgTpl,
          getAnchorInterpolationVariable(d.data.anchor, anchorFields)
        );
        if (aggregation) {
          const aggParams: IExperienceMessagePerCohortAggregated = {
            type: 'EXPERIENCE_MESSAGE_PER_COHORT_AGGREGATED',
            props: {
              msg,
              org: denormalizedExperience.data.org,
              tpl: {
                // This could also be read from the denormalized experience,
                title: d.data.title,
                contacts: d.data.contacts,
                tz: d.data.tz
              },
              accessTokens: [
                denormalizedExperience.id,
                denormalizedExperience.id
              ],
              sender: {
                from: { name: msgTpl.customSender?.name || '', email: '' },
                replyTo: msgTpl.customSender?.replyTo
              },
              preview: {
                sender: {
                  name: toFullName(currUser)
                },
                isPreview: true
              }
            }
          };
          return aggParams;
        } else {
          const unnestedParams: IExperienceMessagePerCohortUnnested = {
            type: 'EXPERIENCE_MESSAGE_PER_COHORT_UNNESTED',
            props: {
              items: [
                {
                  msg,
                  participant: denormalizedExperience.data.participant,
                  accessToken: denormalizedExperience.id
                },
                {
                  msg,
                  participant: denormalizedExperience.data.participant,
                  accessToken: denormalizedExperience.id
                }
              ],
              subject: msg.subject,

              org: denormalizedExperience.data.org,
              tpl: {
                // This could also be read from the denormalized experience,
                title: d.data.title,
                contacts: d.data.contacts,
                tz: d.data.tz
              },
              sender: {
                from: { name: msgTpl.customSender?.name || '', email: '' },
                replyTo: msgTpl.customSender?.replyTo
              },
              preview: {
                sender: {
                  name: toFullName(currUser)
                },
                isPreview: true
              }
            }
          };
          return unnestedParams;
        }
      }

      const ps: IExperienceGenericMessageMail = {
        type: 'EXPERIENCE_MESSAGE',
        props: {
          msg,
          experience: denormalizedExperience,
          org,
          preview: {
            sender: {
              name: toFullName(currUser)
            },
            isPreview: true
          }
        }
      };
      return ps;
    };

    const trackTestSend = (channel: 'Slack' | 'Email' | 'Teams' | 'SMS') => {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        name: 'Timeline Item Test Sent',
        Explanation:
          'Admin triggers a test to be sent for a particular Task or Message',
        'Experience Template ID': d.id,
        'Timeline Item Title': msgTpl.subject,
        'Timeline Item ID': msgTpl.id,
        'Timeline Item Type': 'MESSAGE',
        Channel: channel
      });
    };
    return {
      sendTestMail: !hasSlackChannelActor()
        ? async (
            sampleUser: Doc<IUser>,
            recipientEmail: string,
            otherActors: { [actorKey: string]: Doc<IUser> } | null
          ) => {
            const params = await toParams(sampleUser, otherActors);
            const mail = await sendPreviewMail(params, {
              email: recipientEmail
            });
            // TODO - we could also render the mail now

            trackTestSend('Email');
            return mail;
          }
        : undefined,
      sendSlack: slackEnabled
        ? async (
            sampleUser: Doc<IUser>,
            recipientEmail: string,
            otherActors: { [actorKey: string]: Doc<IUser> } | null
          ) => {
            const params = await toParams(sampleUser, otherActors);
            await sendPreviewSlackMessage(
              params,
              {
                email: recipientEmail,
                orgId: sampleUser.data.orgId,
                type: 'email'
              },
              hasSlackChannelActor()
            );
            trackTestSend('Slack');
          }
        : undefined,
      sendTeams:
        teamsEnabled && !hasSlackChannelActor()
          ? async (
              sampleUser: Doc<IUser>,
              recipientEmail: string,
              otherActors: { [actorKey: string]: Doc<IUser> } | null
            ) => {
              const params = await toParams(sampleUser, otherActors);
              if (TEST) {
                return;
              }
              await sendPreviewTeamsMessage(
                params,
                recipientEmail,
                sampleUser.data.orgId
              );
              trackTestSend('Teams');
            }
          : undefined,

      sendSms:
        smsEnabled && !hasSlackChannelActor()
          ? async (
              sampleUser: Doc<IUser>,
              phoneNumber: string,
              otherActors: { [actorKey: string]: Doc<IUser> } | null
            ) => {
              const params = await toParams(sampleUser, otherActors);
              if (TEST) {
                return;
              }
              await sendSmsPreview(params, phoneNumber, sampleUser.data.orgId);
              trackTestSend('SMS');
            }
          : undefined
    };
  }, [d, msgTpl, currUser, slackEnabled, trackEvent, teamsEnabled, smsEnabled]);

  const textVariableActors = getActorsFromBlocks(msgTpl.blocks).actors;
  let recipientActors: { [key: string]: ISupportingActor } = {};

  /* Goes through supporting actors and if actor is either the message template actor
   or is included in the text blocks of the message, then they are part of the supporting
   actor dropdowns */
  for (const [key, value] of Object.entries(d.data.supportingActors)) {
    if (key === msgTpl.actor || textVariableActors.has(key)) {
      recipientActors = { ...recipientActors, [key]: value };
    }
  }

  return (
    <SendTestButton
      newUx={newUx}
      orgId={d.data.orgId}
      recipientActors={recipientActors}
      sendMail={sendTestMail}
      sendSlack={sendSlack}
      sendTeams={sendTeams}
      sendSms={sendSms}
      validateParticipant={(user) => {
        if (
          !hasAudienceWithActiveBlocksForUser(msgTpl.audiences, user, now())
        ) {
          return `${t(
            `This {{ type }} does not apply for {{ name}}'s Target Audience. Please select a different participant.`,
            {
              type: t('message'),
              name: toFullName(user.data)
            }
          )}`;
        }
        return null;
      }}
      additionalInfo={
        showRepeatHint
          ? t(
              'To simulate a per-cohort message, the test email will include multiple versions of the content for the selected sample participant.'
            )
          : undefined
      }
    />
  );
};

const TEST = false;

export const SendTestTaskButton = ({
  d,
  taskTpl,
  disabled,
  newUx = false
}: {
  d: Doc<IExperienceTemplate>;
  taskTpl: ITaskTemplate;
  // TODO - allow other Mail types
  disabled?: boolean;
  newUx?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const currUser = useCurrentUser();
  const { active: activeIntegrations } = useIntegrationsContext();
  const slackEnabled = activeIntegrations.has(Integration.SLACK);
  const { trackEvent } = useProductAnalytics();

  const teamsEnabled = activeIntegrations.has(Integration.MICROSOFT);
  const smsEnabled = useFeatureEnabled('SMS_V1');
  const { sendTestMail, sendSlack, sendTeams, sendSms } = useMemo(() => {
    const toParams = async (
      sampleUser: Doc<IUser>,
      otherActors: { [actorKey: string]: Doc<IUser> } | null
    ) => {
      const i = d.data.tasks.findIndex((tTpl) => tTpl.id === taskTpl.id);
      const nextTasks = [...d.data.tasks];
      if (i === -1) {
        nextTasks.push(taskTpl);
      } else {
        nextTasks[i] = taskTpl;
      }
      const adjustedTemplate: Doc<IExperienceTemplate> = {
        ...d,
        data: {
          ...d.data,
          tasks: nextTasks
        }
      };
      const n = now();
      // we just want to compute where this item would sit in the timeline
      // we won't take any anchoring into account on purpose to keep this simpler

      const timing = getFixedOrConvertToTaskFixedTimingConsideringTriggerChain(
        taskTpl.timing,
        d.data
      );
      const { start } = getFreshTaskDates(
        timing,
        n,
        {
          type: ExperienceAnchorType.IMMEDIATE,
          offset: { type: 'ABSOLUTE', duration: 0, hour: 9 },
          autoEnrollmentRejectionThreshold: null,
          v: 2
        },
        d.data.tz
      );
      const diffToNow = start.toMillis() - n.toMillis();
      const adjustedStartsAt = addToTs(n, -diffToNow);
      const allActorsWithCustomChoices = await getAllActors(d, sampleUser).then(
        (acs) =>
          acs.map((actor) => {
            if (otherActors?.[actor.key]) {
              return {
                key: actor.key,
                user: otherActors[actor.key]
              };
            }
            return actor;
          })
      );

      const instance = createExperienceInstance(
        d,
        allActorsWithCustomChoices,
        adjustedStartsAt,
        now(),
        { type: EnrollSourceType.UNKNOWN },
        { includeTriggeredItemsAsFixedTimmingItems: true }
      );
      // the instance is not completely correctly configure, e.g.
      // it doesn't track its events correctly and it doesn't track
      // subtask and automation stats correctly.
      // All of this is not needed to send the email though, let's
      // see how long we'll get away with it
      adjustedTemplate.data.status = ExperienceTemplateStatus.ACTIVE;
      mockRunningInstanceState(instance, n);
      const testedTask = instance.data.tasks.find(
        (tsk) => tsk.templateId === taskTpl.id
      );
      if (testedTask && testedTask.status !== TaskStatus.RUNNING) {
        // When a task has a pinned start time associated with it, it might not be marked as running
        // correctly. Hard to do in mockRunningInstanceState - we forcefully set the task under test to running in this case.
        console.log('Set task to running', testedTask);
        testedTask.status = TaskStatus.RUNNING;
      }
      const knownActorsByKey = keyKnownActorsByKey(allActorsWithCustomChoices);

      const org = {
        id: currUser.org.id,
        collection: FS.orgs,
        data: currUser.org
      };
      const denormalizedExperience = toDenormalizedExperience(
        taskTpl.actor,
        instance,
        adjustedTemplate,
        org,
        knownActorsByKey,
        {},
        { ignoreCurrentTime: true, includeSkippedItems: true }
      );

      if (taskTpl.batchMode === BatchMode.PER_COHORT) {
        const item: PerCohortTaskItem = {
          accessToken: denormalizedExperience.id,
          participant: denormalizedExperience.data.participant,
          instanceId: instance.id,
          task: denormalizedExperience.data.tasks.find(
            (tsk) => tsk.templateId === taskTpl.id
          )! // TODO make sage
        };
        const perCohortParams: ITaskStartPerCohort = {
          type: 'TASK_START_PER_COHORT',
          props: {
            tpl: {
              id: d.id,
              title: d.data.title,
              contacts: d.data.contacts,
              tz: d.data.tz
            },
            subject: `[${d.data.title}] ${taskTpl.title}`,
            items: [item, item],
            salutation: denormalizedExperience.data.salutation,
            actor: denormalizedExperience.data.user,
            org: denormalizedExperience.data.org,
            orgId: org.id,
            sender: {
              from: { name: taskTpl.customSender?.name || '', email: '' },
              replyTo: taskTpl.customSender?.replyTo
            },
            preview: {
              sender: {
                name: toFullName(currUser)
              },
              isPreview: true
            }
          }
        };
        return perCohortParams;
      }

      const ps: ITaskStartMail = {
        type: 'TASK_START',
        props: {
          experience: denormalizedExperience,
          org,
          taskIds: instance.data.tasks
            .filter((task) => task.status === TaskStatus.RUNNING)
            .map((task) => task.id),
          preview: {
            sender: {
              name: toFullName(currUser)
            },
            isPreview: true
          }
        }
      };

      return ps;
    };

    const trackTestSend = (channel: 'Slack' | 'Email' | 'Teams' | 'SMS') => {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        name: 'Timeline Item Test Sent',
        Explanation:
          'Admin triggers a test to be sent for a particular Task or Message',
        'Experience Template ID': d.id,
        'Timeline Item Title': taskTpl.title,
        'Timeline Item ID': taskTpl.id,
        'Timeline Item Type': 'TASK',
        Channel: channel
      });
    };

    return {
      sendTestMail: async (
        sampleUser: Doc<IUser>,
        recipientEmail: string,
        otherActors: { [actorKey: string]: Doc<IUser> } | null
      ) => {
        const params = await toParams(sampleUser, otherActors);
        if (TEST) {
          return;
        }
        const mail = await sendPreviewMail(params, { email: recipientEmail });
        console.log('MAIL DATA:', mail);
        // TODO - we could also render the mail now
        trackTestSend('Email');
        return mail;
      },
      sendSlack: slackEnabled
        ? async (
            sampleUser: Doc<IUser>,
            recipientEmail: string,
            otherActors: { [actorKey: string]: Doc<IUser> } | null
          ) => {
            const params = await toParams(sampleUser, otherActors);
            if (TEST) {
              return;
            }
            await sendPreviewSlackMessage(
              params,
              {
                email: recipientEmail,
                orgId: sampleUser.data.orgId,
                type: 'email'
              },
              false
            );
            trackTestSend('Slack');
          }
        : undefined,
      sendTeams: teamsEnabled
        ? async (
            sampleUser: Doc<IUser>,
            recipientEmail: string,
            otherActors: { [actorKey: string]: Doc<IUser> } | null
          ) => {
            const params = await toParams(sampleUser, otherActors);
            if (TEST) {
              return;
            }
            await sendPreviewTeamsMessage(
              params,
              recipientEmail,
              sampleUser.data.orgId
            );
            trackTestSend('Teams');
          }
        : undefined,

      sendSms: smsEnabled
        ? async (
            sampleUser: Doc<IUser>,
            phoneNumber: string,
            otherActors: { [actorKey: string]: Doc<IUser> } | null
          ) => {
            const params = await toParams(sampleUser, otherActors);
            if (TEST) {
              return;
            }
            await sendSmsPreview(params, phoneNumber, sampleUser.data.orgId);
            trackTestSend('SMS');
          }
        : undefined
    };
  }, [
    d,
    taskTpl,
    currUser,
    slackEnabled,
    trackEvent,
    teamsEnabled,
    smsEnabled
  ]);

  const textVariableActors = getActorsFromBlocks(taskTpl.blocks).actors;
  let recipientActors: { [key: string]: ISupportingActor } = {};

  /* Goes through supporting actors and if actor is either the message template actor
   or is included in the text blocks of the message, then they are part of the supporting
   actor dropdowns */
  for (const [key, value] of Object.entries(d.data.supportingActors)) {
    if (key === taskTpl.actor || textVariableActors.has(key)) {
      recipientActors = { ...recipientActors, [key]: value };
    }
  }

  return (
    <SendTestButton
      newUx={newUx}
      orgId={d.data.orgId}
      recipientActors={recipientActors}
      sendMail={sendTestMail}
      sendSlack={sendSlack}
      sendTeams={sendTeams}
      sendSms={sendSms}
      validateParticipant={(user) => {
        if (
          !hasAudienceWithActiveBlocksForUser(taskTpl.audiences, user, now())
        ) {
          return `${t(
            `This {{ type }} does not apply for {{ name}}'s Target Audience. Please select a different participant.`,
            {
              type: t('task'),
              name: toFullName(user.data)
            }
          )}`;
        }
        return null;
      }}
      additionalInfo={
        taskTpl.batchMode === BatchMode.PER_COHORT
          ? t(
              'To simulate a per-cohort task, the test email will include multiple versions of the content for the selected sample participant.'
            )
          : undefined
      }
    />
  );
};
